/**
 * Registering a phone (F4.1-d). The phone shows its install id (a UUIDv7 from
 * getOrCreateDeviceUid) on its setup screen as "Phone code"; the office types it here and it must
 * reach POST /devices exactly as the phone stores it — the phone later finds itself by that value.
 */

export type DeviceTeamKind = 'VILLAGE' | 'SITE'

export interface DeviceForm {
  deviceCode: string
  teamKind: DeviceTeamKind
  groupId: string
  locationId: string
  operatorId: string | null
  codePrefix: string
}

export interface DeviceBody {
  deviceUid: string
  groupId?: string
  locationId?: string
  operatorId?: string
  codePrefix?: string
}

export const emptyDeviceForm = (): DeviceForm => ({
  deviceCode: '',
  teamKind: 'VILLAGE',
  groupId: '',
  locationId: '',
  operatorId: null,
  codePrefix: '',
})

const HEX32 = /^[0-9a-f]{32}$/
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/

/**
 * What was typed → the phone's stored id. Spaces and line breaks (copied from a message, read out
 * in groups) are dropped; a UUID is lower-cased, and one typed without its dashes gets them back.
 * Anything else (older installs) is kept as typed.
 */
export function normalizeDeviceCode(input: string): string {
  const compact = input.replace(/\s+/g, '')
  const lower = compact.toLowerCase()
  if (UUID.test(lower)) return lower
  if (HEX32.test(lower))
    return `${lower.slice(0, 8)}-${lower.slice(8, 12)}-${lower.slice(12, 16)}-${lower.slice(16, 20)}-${lower.slice(20)}`
  return compact
}

/** Short form for tables: the last 8 characters (the random part of a UUIDv7). */
export function shortDeviceCode(uid: string): string {
  return uid.length > 12 ? `…${uid.slice(-8)}` : uid
}

/** Code prefix as typed → server form (upper case, no spaces). */
export const normalizePrefix = (input: string) => input.replace(/\s+/g, '').toUpperCase()

export const PREFIX_PATTERN = /^[A-Z0-9]{1,4}$/

export type DeviceFormErrors = Partial<Record<'deviceCode' | 'team' | 'codePrefix', string>>

export function validateDeviceForm(f: DeviceForm): DeviceFormErrors {
  const e: DeviceFormErrors = {}
  const uid = normalizeDeviceCode(f.deviceCode)
  if (!uid) e.deviceCode = 'Type the code shown on the phone'
  else if (uid.length > 64) e.deviceCode = 'That is longer than a phone code (64 characters at most)'
  else if (/^[0-9a-f-]+$/i.test(uid) && uid.includes('-') && !UUID.test(uid) && uid.length >= 30)
    e.deviceCode = 'This looks like a phone code with a character missing or extra — check it again'
  if (f.teamKind === 'VILLAGE' && !f.groupId) e.team = 'Choose the village'
  if (f.teamKind === 'SITE' && !f.locationId) e.team = 'Choose the store or site'
  const prefix = normalizePrefix(f.codePrefix)
  if (prefix && !PREFIX_PATTERN.test(prefix)) e.codePrefix = '1 to 4 letters or digits, e.g. B2'
  return e
}

/** Form → request body: exactly one of groupId / locationId, optional fields left out when empty. */
export function deviceBody(f: DeviceForm): DeviceBody {
  const prefix = normalizePrefix(f.codePrefix)
  return {
    deviceUid: normalizeDeviceCode(f.deviceCode),
    ...(f.teamKind === 'VILLAGE' ? { groupId: f.groupId } : { locationId: f.locationId }),
    ...(f.operatorId ? { operatorId: f.operatorId } : {}),
    ...(prefix ? { codePrefix: prefix } : {}),
  }
}

/** Team role names → words. */
const ROLE_LABEL: Record<string, string> = {
  village_operator: 'Village operator',
  supervisor: 'Supervisor',
  store_keeper: 'Store keeper',
  machine_operator: 'Machine operator',
}
export const teamRoleLabel = (role: string) =>
  ROLE_LABEL[role] ?? role.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())
